/**
 * 단계별 수치 단일 출처 (hero.ts 의 케이스별 단일 출처 패턴과 동일).
 *
 * Dashboard / Sidebar / AI 운영 관리 / 목록 페이지 summary 가 모두 이 파일에서 가져온다.
 * 한 곳만 수정하면 전 화면 숫자가 동시에 갱신된다.
 *
 * 구조 (모든 단계 공통):
 *   pending     — 미결 / 현재 작업 중 (sidebar 뱃지 · dashboard count · list.length 목표)
 *   urgent      — 긴급 (slaWarn · urgency === "high")
 *   dailyTotal  — 당일 누적 처리 (Dashboard 상단 · KPI · Agent meta funnel)
 *   alerts.*    — dashboard 카드 하단 분류
 *   agents.*    — AI 운영 관리 페이지 단계별 에이전트 meta
 *   summary.*   — 목록 페이지 상단 summary 카드 전용 수치
 *
 * funnel: intake 1284 → processing 1266 → review 1224 → evidence 1224 → payment 974 → post 926
 * ref:    `/.ref/claimQ-designer2/data.js` PIPELINE_DATA / AGENTS 매핑.
 */

export const STAGE_NUMBERS = {
  intake: {
    pending: 18,
    urgent: 3,
    dailyTotal: 1284,
    alerts: {
      /** 누락서류 — 추가서류 요청 발송 후 미응답 */
      missingDocs: 12,
      /** 저화질 — 초해상화 처리 대기 */
      lowQuality: 6,
    },
    agents: {
      /** 접수 자동 처리 — 필수서류 누락/저화질/중복 확인 (당일 누적) */
      autoIntake: 1284,
      /** 자동탐지(추가서류) — 요청필요 = pending */
      docsRequest: 18,
    },
    summary: {
      /** 정상 접수 — 누락/저화질/긴급 제외 (정보처리 진입 예정) */
      normal: 8,
    },
  },
  processing: {
    pending: 42,
    urgent: 0,
    dailyTotal: 1266,
    alerts: {
      /** 인식·추출실패 — ref 인식실패(12)+추출실패(15) 합산 */
      extractFail: 27,
      /** 검증실패 */
      validationFail: 15,
    },
    agents: {
      /** 문서 정보 추출 완료 (당일 누적) */
      docExtract: 1266,
      /** OCR 값 비교 — 저신뢰 = pending */
      ocrVerify: 42,
    },
    summary: {
      /** 저신뢰 OCR — 필수 검수 대상 */
      lowConfOcr: 12,
      /** OCR 처리중 — 평균 1.4분 진행 */
      ocrProcessing: 18,
    },
  },
  review: {
    pending: 250,
    urgent: 2,
    dailyTotal: 1224,
    alerts: {
      /** 일반심사 */
      general: 165,
      /** 고액심사 */
      highValue: 85,
    },
    agents: {
      /** AI 산출 — 기지급·계약 기반 예상 지급금 (당일 누적) */
      aiEstimate: 1224,
      /** 지급 판단 지원 — 자동배정 / dashboard 배분현황 "심사 경로" 합계 */
      judgementAssist: 561,
    },
    summary: {
      /** 의료심사 이관 — 회신 대기 */
      medicalReferral: 8,
      /** 금일 처리 완료 */
      doneToday: 142,
    },
  },
  evidence: {
    pending: 250,
    urgent: 0,
    dailyTotal: 1224,
    alerts: {
      /** 일반심사 */
      general: 165,
      /** 고액심사 */
      highValue: 85,
    },
    agents: {
      /** 지급 확정 — 정보요약·지급사유·금액 3단 자동 생성 (당일 누적) */
      reasonGen: 1224,
    },
    summary: {
      /** 수정 중 — 심사자 코멘트 적용 */
      editing: 4,
      /** 생성 완료 — AI 신뢰도 96% */
      generated: 9,
      /** 확정 — 금일 확정 */
      finalized: 9,
    },
  },
  payment: {
    pending: 48,
    urgent: 1,
    dailyTotal: 974,
    alerts: {
      /** 부책 */
      covered: 29,
      /** 일부지급 */
      partial: 19,
    },
    agents: {
      /** 지급 완료 산출 — 최종 산출금·감액·공제·지급금 계산 (당일 결제 완료) */
      finalAmount: 926,
      /** 고객 안내 발송 — 보험금지급 안내장 */
      notifyPayment: 926,
      /** 고객 안내 발송 — 조사 안내장 */
      notifyInvestigation: 12,
    },
    summary: {
      /** 지급 예정 */
      planned: 41,
      /** 결제 실패 */
      paymentFail: 0,
      /** 고액 건 — 사전 통보 */
      highValue: 7,
      /** 지급 보류 */
      onHold: 0,
      /** 지급 예정 합계 (억) — sub 텍스트용 */
      plannedAmountBillions: 0.6,
    },
  },
  post: {
    pending: 16,
    urgent: 0,
    dailyTotal: 926,
    alerts: {
      /** 이상패턴탐지 */
      anomaly: 14,
      /** 민원연계이의제기접수 */
      voc: 2,
    },
    agents: {
      /** 지급 QA 점검 — QA 대상 선별 및 이상지급 확인 (당일 누적) */
      qaCheck: 926,
      /** 운영 보고서 — 보고서 작성 = pending */
      reportGen: 16,
    },
    summary: {
      /** QA 점검 — 무작위 + 위험 (= pending) */
      qa: 12,
      /** SIU 이관 — 조사 의뢰 */
      siuReferral: 1,
      /** 면책 — 부지급 결정 */
      exempt: 1,
    },
  },
} as const;

export type StageId = keyof typeof STAGE_NUMBERS;
